import { z } from 'zod'
import type { TransitionTable } from '../helpers/state-machine.js'

/**
 * A user's lifecycle state. Mirrors the `UserStatus` enum in the Prisma schema
 * — the two must stay in sync. PENDING: invited, hasn't set a password yet;
 * ACTIVE: can sign in; DEACTIVATED: sign-in blocked but the record is kept.
 */
export const USER_STATUSES = ['PENDING', 'ACTIVE', 'DEACTIVATED'] as const

export const userStatusSchema = z.enum(USER_STATUSES)

export type UserStatus = z.infer<typeof userStatusSchema>

/**
 * Invited, or able to sign in. A deactivated account is kept and is not one of
 * these, so a new status is not counted until it is named here.
 */
export const PRESENT_USER_STATUSES = ['PENDING', 'ACTIVE'] as const satisfies readonly UserStatus[]

/**
 * The moves an account may make.
 *
 * - `PENDING → ACTIVE` is accepting an invitation, which sets a password.
 * - `PENDING → DEACTIVATED` and `ACTIVE → DEACTIVATED` block sign-in.
 * - `DEACTIVATED → ACTIVE` is reactivating someone who has a password, and
 *   `DEACTIVATED → PENDING` someone who never set one: they go back to being
 *   invited. Reactivation is a deliberate act with its own button, so both are
 *   declared rather than merely not forbidden.
 *
 * **`ACTIVE → PENDING` is missing.** An account that has been used is never
 * "invited" again; it can only be blocked and unblocked. An account that starts
 * ACTIVE — signing up — has no PENDING before it, and its history says so.
 */
export const USER_STATUS_TRANSITIONS: TransitionTable<UserStatus> = {
  PENDING: ['ACTIVE', 'DEACTIVATED'],
  ACTIVE: ['DEACTIVATED'],
  DEACTIVATED: ['ACTIVE', 'PENDING'],
}
